import React from 'react';
import { BookOpen, Users, Cog, TrendingUp, CheckCircle, AlertTriangle, DollarSign, FileText, Shield, Play } from 'lucide-react';
import './Home.css'; // Assuming shared styles

const InvestorGuide = () => {
  return (
    <div className="homeContainer" style={{paddingTop: '4rem'}}>
      <section className="faq bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h1 className="sectionTitle flex items-center justify-center space-x-3">
              <BookOpen className="w-8 h-8 text-primary" />
              <span>📘 User Guide: Fractional Ownership in Real Estate</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              (For Educational & Customer Use – Delhi NCR Projects)
            </p>
          </div>
          <div className="prose prose-lg max-w-none bg-card/30 backdrop-blur-sm rounded-3xl p-8 border border-border/50">
            <div className="flex items-center space-x-3 mb-6">
              <BookOpen className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">1. Introduction</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Real estate is one of the most trusted asset classes in India. However, high capital requirements and complex legal processes make it inaccessible to many investors.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Fractional Ownership solves this problem by allowing multiple investors to co-own a property through a structured, legally compliant entity called a Special Purpose Vehicle (SPV).
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              On our platform, every land parcel or project is held in a Private Limited Company (SPV), and investors receive shares in that SPV proportionate to their investment.
            </p>

            <div className="flex items-center space-x-3 mb-6">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">2. What is Fractional Ownership?</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Fractional ownership means owning a fraction of a high-value property. Instead of buying the entire asset, you invest in a part of it, and your ownership rights are recorded through shares of an SPV.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              <strong>Example:</strong>
            </p>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>Each project is held in its own company (SPV).</li>
              <li>The number of shares, the price per share and the minimum investment are set per project and shown on the deal page to invited investors.</li>
              <li>Your shareholding is your shares as a percentage of all shares allotted in that SPV.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              👉 This gives you legal ownership, proportional returns, and voting rights in decisions made by the SPV.
            </p>

            <div className="flex items-center space-x-3 mb-6">
              <Cog className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">3. How Does It Work?</h2>
            </div>
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Step 1 – Selection</h3>
                <p className="text-muted-foreground leading-relaxed mb-3">Choose a curated land parcel from the platform. Each listing includes:</p>
                <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
                  <li>Location map</li>
                  <li>Legal documents (title, approvals)</li>
                  <li>Fraction price</li>
                  <li>Expected exit strategy</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Step 2 – Investment</h3>
                <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
                  <li>Register and complete KYC (PAN, Aadhaar, Bank Details).</li>
                  <li>Select number of fractions.</li>
                  <li>Pay via secure Escrow Account linked to the SPV.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Step 3 – Allotment of Shares</h3>
                <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
                  <li>Once the SPV completes land acquisition, you are allotted equity shares in the SPV.</li>
                  <li>Share certificates are digitally issued and recorded in the Ministry of Corporate Affairs (MCA) records.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Step 4 – Holding Period</h3>
                <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
                  <li>Track your investment in your Dashboard (ownership %, project updates, valuation).</li>
                  <li>The SPV holds the land securely until exit.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Step 5 – Exit</h3>
                <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
                  <li>When the land is sold to a builder, developer, or under DDA Land Pooling Policy, the SPV distributes profits to all shareholders.</li>
                  <li>Alternatively, you can sell your shares (fractions) on the platform’s resale marketplace before the final exit.</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center space-x-3 mb-6">
              <CheckCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">4. Benefits of Fractional Ownership</h2>
            </div>
            <ul className="list-none text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>✅ <strong>Affordable Entry</strong> – Start investing with lakhs instead of crores.</li>
              <li>✅ <strong>Diversification</strong> – Spread your capital across multiple land parcels.</li>
              <li>✅ <strong>Liquidity</strong> – Sell your fractions anytime through the resale marketplace.</li>
              <li>✅ <strong>Professional Management</strong> – SPV structure ensures compliance, legal safety, and transparent management.</li>
              <li>✅ <strong>Transparency</strong> – Escrow accounts, MCA filings, and investor dashboards give full visibility.</li>
              <li>✅ <strong>Tangible Asset</strong> – Land is a real, physical asset with long-term value.</li>
              <li>✅ <strong>Inflation Hedge</strong> – Land prices generally rise in line with or above inflation.</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">5. Risks You Should Know</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Like all investments, fractional real estate carries risks:
            </p>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-6 space-y-2">
              <li><strong>Market Risk</strong> – Land value may not always appreciate.</li>
              <li><strong>Regulatory Risk</strong> – Government or DDA policy changes may impact timelines.</li>
              <li><strong>Execution Risk</strong> – Uncertainty or delays in project execution may extend holding period.</li>
              <li><strong>Liquidity Risk</strong> – Resale depends on demand; selling quickly may not always be possible.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              👉 We mitigate risks via legal due diligence, escrow banking, SPV structures, and curated property selection — but no investment is risk-free.
            </p>

            <div className="flex items-center space-x-3 mb-6">
              <DollarSign className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">6. Platform Fees</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>The fee that applies depends on the type of deal and is stated on each project's deal page.</li>
              <li>Resale between investors carries a fee, shown before you confirm.</li>
              <li>Project expenses are recovered at cost and shown to shareholders line by line.</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">7. Taxation</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>Profits from sale or resale are subject to capital gains tax under Indian Income Tax laws.</li>
              <li>Dividends (if declared) may also attract taxation.</li>
              <li>Investors receive annual statements to assist in filing.</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <TrendingUp className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">8. Example Scenario</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>[WORKED EXAMPLE — TO BE PROVIDED]</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">9. Safety Features</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>Private Limited SPV ensures limited liability.</li>
              <li>Escrow Accounts protect funds until land acquisition.</li>
              <li>Digital contracts and MCA filings document your ownership.</li>
              <li>Audited Accounts ensure financial transparency.</li>
              <li>Regular Updates keep investors informed on progress.</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <Play className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">10. Getting Started</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed mb-8 space-y-2">
              <li>Register on the platform.</li>
              <li>Complete KYC verification.</li>
              <li>Browse Projects and select fractions.</li>
              <li>Invest via Escrow Account.</li>
              <li>Receive SPV Share Allotment.</li>
              <li>Track & Exit via dashboard or resale marketplace.</li>
            </ul>

            <div className="flex items-center space-x-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">11. Important Note</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Real estate investments are subject to market, regulatory, and execution risks. While land is considered a stable and appreciating asset, returns are not guaranteed. Please read all project documents and consult financial/legal advisors before investing.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestorGuide;
